import {
  CaretDownOutlined,
  CaretUpOutlined,
  CloseOutlined,
  FilterOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { Checkbox, Popconfirm, Popover, Tooltip } from 'antd'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import Loader from '@/components/Atoms/Loader/Loader'
import { useUserInfoStore } from '@/stores/user/userInfoStore'

import type { BackendSort } from '../../AttributeProcessing.types'
import { useAttributeProcessingContext } from '../../AttributeProcessingContext'
import FiltersPanel from './FiltersPanel/FiltersPanel'
import cn from './LeftPanel.module.scss'

const LABEL_WITH_NOTE = /^(.+?)\s+(\(.+\))$/

function HeaderLabel({ label }: { label: string }) {
  const match = LABEL_WITH_NOTE.exec(label)
  if (!match) return label
  return (
    <span className={cn.sortLabelText}>
      {match[1]}
      <span className={cn.sortSubLabel}>{match[2]}</span>
    </span>
  )
}

function LeftPanel() {
  const {
    query,
    setQuery,
    loading,
    handleSearch,
    hasSearched,
    hasActiveFilters,
    showFilters,
    setShowFilters,
    totalRows,
    selectedIds,
    selectedPreviewRows,
    setSelectedIds,
    setSelectedDetails,
    setDynamicColumnLabel,
    allPageSelected,
    somePageSelected,
    handleSelectAll,
    rows,
    sortedRows,
    backendSort,
    handleBackendSort,
    dynamicSortDir,
    handleDynamicSort,
    loadingMore,
    loadMoreRef,
    handleRowClick,
    toggleOne,
    removeRow,
    computeDynamicCellValue,
    dynamicColumnLabel,
    activeTab,
    selectedMainField,
    selectedChrcCode,
  } = useAttributeProcessingContext()

  const isDynamicSortable =
    selectedIds.size >= 2 &&
    ((activeTab === 'MAIN' && selectedMainField !== null) ||
      (activeTab === 'ADDITIONAL' && selectedChrcCode !== null))
  const selectedOrganisation = useUserInfoStore(
    (state) => state.selectedOrganisation
  )
  const isOrganisationSelected = Object.keys(selectedOrganisation).length > 0
  const [selectionPopoverOpen, setSelectionPopoverOpen] = useState(false)
  const [copiedInfo, setCopiedInfo] = useState<{
    token: number
    x: number
    y: number
  } | null>(null)

  const handleCopyArticle = (article: string, target: HTMLElement) => {
    const rect = target.getBoundingClientRect()
    navigator.clipboard.writeText(article).then(() => {
      const token = Date.now()
      setCopiedInfo({ token, x: rect.left + rect.width / 2, y: rect.bottom })
      setTimeout(() => {
        setCopiedInfo((current) => (current?.token === token ? null : current))
      }, 1500)
    })
  }

  useEffect(() => {
    if (!copiedInfo) return

    const hideOnScroll = () => setCopiedInfo(null)
    window.addEventListener('scroll', hideOnScroll, {
      capture: true,
      passive: true,
    })

    return () => {
      window.removeEventListener('scroll', hideOnScroll, true)
    }
  }, [copiedInfo])

  useEffect(() => {
    if (selectedIds.size === 0) setSelectionPopoverOpen(false)
  }, [selectedIds.size])

  const selectionPreview =
    selectedPreviewRows.length > 0 ? (
      <div className={cn.selectionPreview}>
        {selectedPreviewRows.map((row) => (
          <div key={row.id} className={cn.selectionPreviewItem}>
            <strong>{row.article || row.code}</strong>
            <span>{row.brandName}</span>
            <span>{row.description}</span>
            <Popconfirm
              title="Снять выделение с товара?"
              okText="Да"
              cancelText="Нет"
              okButtonProps={{
                style: {
                  backgroundColor: 'var(--orange-1)',
                  borderColor: 'var(--orange-1)',
                },
              }}
              onConfirm={(e) => {
                e?.stopPropagation()
                setSelectedIds((prev) => {
                  const next = new Set(prev)
                  next.delete(row.id)
                  return next
                })
              }}
              onCancel={(e) => e?.stopPropagation()}
            >
              <button
                className={cn.selectionPreviewRemove}
                onClick={(e) => e.stopPropagation()}
                title="Снять выделение"
                type="button"
              >
                <CloseOutlined />
              </button>
            </Popconfirm>
          </div>
        ))}
      </div>
    ) : null

  const backendSortHeader = (label: string, col: keyof BackendSort) => {
    const dir = backendSort[col]
    const icon = dir ? (
      dir === 'asc' ? (
        <CaretUpOutlined className={cn.sortIcon} />
      ) : (
        <CaretDownOutlined className={cn.sortIcon} />
      )
    ) : (
      <span className={cn.sortIconInactive}>↕</span>
    )
    return (
      <span className={cn.sortLabel} onClick={() => handleBackendSort(col)}>
        <span className={cn.sortSpacer} />
        <HeaderLabel label={label} />
        {icon}
      </span>
    )
  }

  const dynamicSortHeader = (label: string) => {
    if (!isDynamicSortable) return <HeaderLabel label={label} />
    const icon = dynamicSortDir ? (
      dynamicSortDir === 'asc' ? (
        <CaretUpOutlined className={cn.sortIcon} />
      ) : (
        <CaretDownOutlined className={cn.sortIcon} />
      )
    ) : (
      <span className={cn.sortIconInactive}>↕</span>
    )
    return (
      <span className={cn.sortLabel} onClick={handleDynamicSort}>
        <span className={cn.sortSpacer} />
        <HeaderLabel label={label} />
        {icon}
      </span>
    )
  }

  return (
    <div className={cn.leftPanel}>
      {/* Search bar */}
      <div className={cn.searchBar}>
        <input
          className={cn.searchInput}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Артикул, код или название..."
        />
        <button
          className={cn.btnPrimary}
          onClick={handleSearch}
          disabled={loading || (!hasActiveFilters && !query.trim())}
        >
          {loading ? 'Поиск...' : 'Найти'}
        </button>
        <button
          className={[cn.btnSecondary, showFilters ? cn.btnSecondaryActive : '']
            .filter(Boolean)
            .join(' ')}
          onClick={() => setShowFilters((v) => !v)}
        >
          <FilterOutlined />
          {showFilters ? 'Скрыть фильтры' : 'Показать фильтры'}
          {hasActiveFilters && <span className={cn.filterBadge} />}
        </button>
      </div>

      {showFilters && <FiltersPanel />}

      <div className={cn.tableInfo}>
        <span className={cn.totalCount}>
          {hasSearched.current && (
            <>
              Найдено: <strong>{totalRows}</strong>
            </>
          )}
        </span>
        {hasSearched.current && (
          <div
            className={[
              cn.selectionInfo,
              selectedIds.size === 0 ? cn.selectionInfoHidden : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <Popover
              content={selectionPreview}
              title="Выбранные элементы"
              placement="bottomLeft"
              mouseEnterDelay={0.2}
              overlayClassName={
                isOrganisationSelected
                  ? [cn.selectionPopover, 'organisationSelected'].join(' ')
                  : cn.selectionPopover
              }
              open={selectionPopoverOpen && selectedIds.size > 0}
              onOpenChange={(open) =>
                setSelectionPopoverOpen(open && selectedIds.size > 0)
              }
            >
              <span className={cn.selectionCount}>
                Выбрано: <strong>{selectedIds.size}</strong>
              </span>
            </Popover>
            <button
              className={cn.clearSelectionBtn}
              onClick={() => {
                setSelectedIds(new Set())
                setSelectedDetails([])
                setDynamicColumnLabel('Выбранный реквизит')
              }}
            >
              Снять выделение
            </button>
          </div>
        )}
      </div>

      <div className={cn.tableWrap}>
        <div className={cn.tableHeader}>
          <div
            className={cn.colCheck}
            onClick={handleSelectAll}
            title="Выбрать / снять все"
          >
            <Checkbox
              checked={allPageSelected}
              indeterminate={somePageSelected}
            />
          </div>
          <div className={cn.colBrand}>
            <span>Бренд</span>
          </div>
          <div
            className={[
              cn.colArticle,
              cn.colSortable,
              backendSort.article && cn.colSorted,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {backendSortHeader('Артикул (производителя)', 'article')}
          </div>
          <div
            className={[
              cn.colName,
              cn.colSortable,
              backendSort.description && cn.colSorted,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {backendSortHeader('Наименование', 'description')}
          </div>
          <div
            className={[
              cn.colDynamic,
              isDynamicSortable && cn.colSortable,
              dynamicSortDir && cn.colSorted,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {dynamicSortHeader(dynamicColumnLabel)}
          </div>
        </div>

        {loading ? (
          <div className={cn.tableCenter}>
            <Loader size={32} />
          </div>
        ) : !hasSearched.current ? (
          <div className={cn.tableCenter}>
            <span>Введите запрос и нажмите «Найти»</span>
          </div>
        ) : rows.length === 0 ? (
          <div className={cn.tableCenter}>
            <span>Совпадений не найдено</span>
          </div>
        ) : (
          <div className={cn.tableBody}>
            {sortedRows.map((row, idx) => {
              const isSel = selectedIds.has(row.id)
              return (
                <div
                  key={row.id}
                  className={[
                    cn.tableRow,
                    isSel ? cn.tableRowSelected : '',
                    row.flashStatus === 'success' ? cn.flashSuccess : '',
                    row.flashStatus === 'error' ? cn.flashError : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={(e) => handleRowClick(row.id, idx, e)}
                >
                  <div
                    className={cn.colCheck}
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleOne(row.id)
                    }}
                  >
                    <Checkbox checked={isSel} />
                    {row.flashStatus === 'error' && row.errorMessage && (
                      <Tooltip title={row.errorMessage} placement="right">
                        <WarningOutlined className={cn.errorIcon} />
                      </Tooltip>
                    )}
                  </div>
                  <div className={cn.colBrand}>
                    <span title={row.brandName}>{row.brandName}</span>
                  </div>
                  <div className={cn.colArticle}>
                    {row.article && (
                      <div
                        className={cn.articleValue}
                        title="Скопировать артикул"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleCopyArticle(row.article, e.currentTarget)
                        }}
                      >
                        {row.article}
                      </div>
                    )}
                  </div>
                  <div className={cn.colName}>
                    <Tooltip
                      title={row.description || undefined}
                      placement="top"
                      mouseEnterDelay={0.3}
                    >
                      <span>{row.description}</span>
                    </Tooltip>
                  </div>
                  <div className={cn.colDynamic}>
                    {(() => {
                      const v = computeDynamicCellValue(row)
                      return <span title={v}>{v}</span>
                    })()}
                  </div>
                  <Popconfirm
                    title="Убрать из списка?"
                    okText="Да"
                    cancelText="Нет"
                    okButtonProps={{
                      style: {
                        backgroundColor: 'var(--orange-1)',
                        borderColor: 'var(--orange-1)',
                      },
                    }}
                    onConfirm={(e) => {
                      e?.stopPropagation()
                      removeRow(row.id)
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                  >
                    <button
                      className={cn.removeRowBtn}
                      onClick={(e) => e.stopPropagation()}
                      title="Убрать из списка"
                    >
                      <CloseOutlined />
                    </button>
                  </Popconfirm>
                </div>
              )
            })}

            <div ref={loadMoreRef} className={cn.loadMoreAnchor}>
              {loadingMore && <Loader size={18} />}
              {!loadingMore && rows.length >= totalRows && rows.length > 0 && (
                <span>Все товары загружены</span>
              )}
            </div>
          </div>
        )}
      </div>

      {copiedInfo &&
        createPortal(
          <div
            key={copiedInfo.token}
            className={cn.copyNotification}
            style={{
              '--copy-x': `${copiedInfo.x}px`,
              '--copy-y': `${copiedInfo.y}px`,
            }}
          >
            скопировано
          </div>,
          document.body
        )}
    </div>
  )
}

export default LeftPanel
